import Router from 'next/router';

// Component
import PriceContainer from 'components/PriceContainer';

// Utils
import { useImageHandler } from 'utils/custom_hooks';
import truncateString from 'utils/truncate_string';
import { cancelEventBubble } from 'utils/cancel_event_bubble';
import { generateCoverImageUrl } from 'utils/generate_cover_image_url';
import { useMemo } from 'react';
import moment from 'moment';
import { truncateText } from 'utils/helper';
import { getClassNameByLang } from 'utils/getClassByLang';
import Button from 'components/Button';
import { BUTTON_TYPE } from 'constants/button';
import { SubHeading, TextBody } from 'components/Typography/General';
import { FONT_TYPES } from 'variables/font_groups';
import { getDirectionByLanguage } from 'utils/get_direction';
import { trackAddToCart, trackCtaClick, trackSelectContent } from 'lib/analytics';
import { isMobile } from 'react-device-detect';

export default function Card({ isProduct, data, slug, handleAddToCart, isAlreadyInCart, filter }) {
  const memoizedOptionList = useMemo(
    () => [data.courseOrganizer?.logo, data.courseOrganizer?.companyLogo],
    [data.courseOrganizer?.logo, data.courseOrganizer?.companyLogo]
  );
  const { currentImage, handleImageToDefault } = useImageHandler({
    optionList: memoizedOptionList,
    data,
  });
  let month = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const handleRedirect = () => {
    const url = !isProduct ? 'courses/' + slug : 'products/' + slug;
    trackSelectContent({
      page_type: 'category',
      item_id: data.courseId || data.productId,
      item_name: data.name,
      item_category: data.categoryName || 'unknown',
      list_name: isProduct ? 'products' : 'courses',
    });
    trackCtaClick({
      page_type: 'category',
      cta_label: 'open_course_detail',
      location: filter ? 'category_filtered_list' : 'category_listing',
      course_id: data.courseId || data.productId,
    });
    Router.push(url);
  };

  const classNameByLang = getClassNameByLang(data?.language);
  const buildAddToCartItem = ({ data, isProduct }) => ({
    item_id: data.courseId || data.productId,
    item_name: data.name,
    item_category: data.categoryName || 'unknown',
    item_variant: isProduct ? 'product' : 'course',
    price: parseFloat(data.price || data.price?.['$numberDecimal'] || 0),
    quantity: 1,
  });

  return (
    <section id="card">
      <div
        className="row cursor-pointer category-card-wrapper card-border-bottom mt-4 rounded"
        onClick={() => !isMobile ? handleRedirect() : {}}
      >
        <div className={`d-flex flex-column ${filter ? 'col-6 col-md-4' : 'col-md-3'}`}>
          <div className="category-card-image-container">
            <img className="category-card-image" src={generateCoverImageUrl(data.picture)} alt="Bedroom organization" />
          </div>
          {!isMobile && (
            <Button
              type={isAlreadyInCart ? BUTTON_TYPE.addedToCartDarker : BUTTON_TYPE.primary}
              variety={3}
              fontType={FONT_TYPES.textBody}
              className="mt-0 fw-normal category-card-button"
              onClick={(e) => {
                cancelEventBubble(e);
                trackAddToCart({
                  page_type: 'category',
                  currency: 'USD',
                  value: parseFloat(data?.price?.$numberDecimal ? data?.price?.$numberDecimal : data?.price || 0),
                  items: [buildAddToCartItem({ data, isProduct })],
                  course_id: data.courseId || data.productId,
                });
                trackCtaClick({
                  page_type: 'category',
                  cta_label: 'add_to_cart',
                  location: 'category_card',
                  course_id: data.courseId || data.productId,
                });
                handleAddToCart();
              }}
            >
              {isAlreadyInCart ? 'Added to cart' : 'Add to cart'}
            </Button>
          )}
        </div>
        <div className={`d-flex flex-column justify-content-between ${filter ? 'col-6 col-md-8' : 'col-12 col-md-9'}`}>
          <div>
            <div className="category-card-user-picture-container">
              <img
                className="category-card-user-picture"
                src={currentImage}
                onError={() => handleImageToDefault()}
                alt={data.courseOrganizer.name}
              />
            </div>
            <SubHeading
              variety={3}
              dir={getDirectionByLanguage(data?.language)}
              style={{ textWrap: 'break-word' }}
              className={`font-weight-600 cursor-pointer category-card-title title-2 ${classNameByLang}`}
            >
              {data.name}
              {/* {truncateText(data.name)} */}
            </SubHeading>
            {data?.dateFrom && data?.dateTo ? (
              <div>
                Course Schedule:
                <p className="font-size-14 text-secondary d-none d-lg-flex">
                  {data?.dateFrom && moment(data.dateFrom).format('MMMM D, YYYY')}
                  {data?.dateFrom && data?.dateTo && ' - '}
                  {data?.dateTo && moment(data.dateTo).format('MMMM D, YYYY')}
                </p> 
              </div>
            ) : (
              <p className="font-size-14 text-secondary d-none d-lg-flex">
                Published: {moment(data.createdAt).format('MMMM D, YYYY')}
              </p>
            )}
            <TextBody
              variety={5}
              dir={getDirectionByLanguage(data?.language)}
              className={`d-none d-lg-flex flex-lg-column ${classNameByLang} custom-text-body`}
              style={{ wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: truncateText(data.description, 200, true) }}
            ></TextBody>
          </div>
          <div className="mt-4 mt-lg-0">
            <div className="d-flex justify-content-between align-items-start flex-lg-row flex-column mb-4 mb-lg-0">
              <span className="category-card-user-name" style={{ color: '#666666' }}>
                {data.courseOrganizer?.name && data.courseOrganizer?.name !== ' '
                  ? data.courseOrganizer?.name
                  : 'KPI Institute'}
              </span>
            </div>
            <PriceContainer
              variableDiscount={[parseFloat(data.discount), parseFloat(data.discount?.['$numberDecimal'])]}
              variablePrice={[parseFloat(data.price), parseFloat(data.price['$numberDecimal'])]}
              courseOrganizer={!isProduct ? data.courseOrganizer?.code : data.provider?.code}
              isLarge
            />
          </div>

        </div>
        {isMobile && (
        <div className="row mt-4 ml-0" style={{marginLeft: "-4px"}}>
          <div className="col-6 m-0 p-1">
            <Button
              type={isAlreadyInCart ? BUTTON_TYPE.addedToCartDarker : BUTTON_TYPE.primary}
              variety={3}
              fontType={FONT_TYPES.textBody}
              className="mt-0 fw-normal category-card-button"
              style={{marginRight:"5px", width: "100%", height: '52px'}}
              onClick={(e) => {
                // cancelEventBubble(e);
                trackAddToCart({
                  page_type: 'category',
                  currency: 'USD',
                  value: parseFloat(data?.price?.$numberDecimal ? data?.price?.$numberDecimal : data?.price || 0),
                  items: [buildAddToCartItem({ data, isProduct })],
                  course_id: data.courseId || data.productId,
                });
                trackCtaClick({
                  page_type: 'category',
                  cta_label: 'add_to_cart',
                  location: 'category_card',
                  course_id: data.courseId || data.productId,
                });
                handleAddToCart();
              }}
            >
              {isAlreadyInCart ? 'Added to cart' : 'Add to cart'}
            </Button>
            </div>
          <div className="col-6 m-0 p-1">
           <Button
              variety={4}
              fontType={FONT_TYPES.subHeading}
              type={BUTTON_TYPE.secondary}
              style={{marginLeft:"5px", width: "100%", height: '52px'}}
              className={`d-lg-none d-inline-block category-card-button p-0 mt-0`}
              onClick={handleRedirect}
            >
              More Information
            </Button> 
          </div>
        </div>
      )}
      </div>
    </section>
  );
}
